import React from 'react';
import { Box, Paper, Typography, Button, Alert, CircularProgress } from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import type { IncidentDrawerDetailScope } from './incidentDrawerDetailTypes';
import { OBSERVATION_EVIDENCE_CLIP_CAPTION } from '../../domain/incidents/observationDispositionCopy';
import { honestClipMissingHelper } from '../../domain/incidents/evidenceClipPresence';

export function IncidentDrawerDetailStep2Clip({ scope }: { scope: IncidentDrawerDetailScope }) {
  const s = scope as Record<string, any>;
  const showClip =
    s.hasClipArtifact ||
    Boolean((s.selectedIncident as any)?.clip_uri) ||
    ['AVAILABLE', 'PENDING', 'FAILED', 'MISSING', 'UNAVAILABLE'].includes(
      String(s.effectiveClipStatus || (s.selectedIncident as any)?.clip_status || '').toUpperCase(),
    ) ||
    s.latestVideoLoading ||
    s.latestVideoError ||
    s.latestVideoObjectUrl;

  if (!showClip) return null;

  return (
    <Paper
      ref={s.evidencePlayerCardRef}
      variant="outlined"
      tabIndex={-1}
      data-testid="incident-evidence-player-card"
      sx={s.drawerCardSx}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, mb: 1.1 }}>
        <Box sx={{ minWidth: 0 }}>
          {!s.groupedReviewMode ? (
            <Typography sx={s.cardLabelSx}>{s.journeyPhaseTitleForStep('step2')} phase</Typography>
          ) : null}
          <Typography sx={s.cardHeadingSx}>{s.groupedReviewMode ? 'Clip' : 'Incident clip'}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {s.latestVideoIdentity?.filename || s.latestVideoAttachment?.filename || 'Latest evidence clip'}
          </Typography>
          {s.relatedIncidentCount > 1 && !s.observationDispositionMode ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {s.reviewingLatestInSeries
                ? `This should be the latest evidence-centered clip in a recurring risk pattern with ${s.relatedIncidentCount.toLocaleString()} observations.`
                : `This clip is representative evidence from a recurring risk pattern with ${s.relatedIncidentCount.toLocaleString()} observations.`}
            </Typography>
          ) : s.observationDispositionMode ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {OBSERVATION_EVIDENCE_CLIP_CAPTION}
            </Typography>
          ) : null}
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          disabled={!s.selectedIncident?.incident_id || (!s.latestVideoObjectUrl && s.effectiveClipStatus !== 'AVAILABLE')}
          onClick={async () => {
            try {
              const blob =
                s.latestVideoAttachment?.filename && s.latestVideoAttachment?.preferFileEndpoint
                  ? await s.fetchEvidenceFileBlob(s.selectedIncident.incident_id, s.latestVideoAttachment.filename)
                  : await s.fetchLatestVideoBlob(s.selectedIncident.incident_id);
              s.downloadBlob(blob, s.latestVideoAttachment?.filename || `${s.selectedIncident.incident_id}_latest_clip.mp4`);
            } catch {
              // ignore
            }
          }}
        >
          Open clip
        </Button>
      </Box>
      {s.latestVideoError ? (
        <Alert severity="warning">{s.latestVideoError}</Alert>
      ) : s.evidenceMomentError ? (
        <Alert severity="warning" data-testid="incident-evidence-moment-error">
          {s.evidenceMomentError}
        </Alert>
      ) : s.latestVideoLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : s.effectiveClipStatus === 'MISSING' || s.effectiveClipStatus === 'UNAVAILABLE' ? (
        <Alert severity="warning" data-testid="incident-evidence-clip-missing">
          {honestClipMissingHelper()}
        </Alert>
      ) : s.effectiveClipStatus === 'PENDING' ? (
        <Alert severity="info">
          The evidence-centered clip is still being prepared. Use <strong>Refresh clip status</strong> to check for the
          latest video without leaving this review.
        </Alert>
      ) : s.latestVideoObjectUrl ? (
        <Box
          component="video"
          ref={s.evidencePlayerRef}
          controls
          src={s.latestVideoObjectUrl}
          tabIndex={0}
          data-testid="incident-evidence-player"
          data-clip-id={s.latestVideoIdentity?.clipId || ''}
          data-filename={s.latestVideoIdentity?.filename || ''}
          onLoadedMetadata={s.handleEvidencePlayerLoadedMetadata}
          onError={s.handleEvidencePlayerError}
          sx={{
            width: '100%',
            maxHeight: 420,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.default',
          }}
        />
      ) : (
        <Alert severity={s.effectiveClipStatus === 'FAILED' ? 'warning' : 'info'}>
          {s.effectiveClipStatus === 'FAILED'
            ? 'Clip evidence failed to load for this incident.'
            : 'Clip evidence is present but not loaded yet.'}
        </Alert>
      )}
      {s.clipTiming.covered === false ? (
        <Alert severity="warning" sx={{ mt: 1.25 }}>
          This clip does not include the evidence target time. It is follow-on context, not the primary evidence clip.
        </Alert>
      ) : null}
      {s.clipTiming.capturedAtMs ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Evidence file written on edge at {s.formatTimingInstant(s.clipTiming.capturedAtMs)}.
        </Typography>
      ) : null}
      {!s.clipTiming.capturedAtMs && s.clipTiming.cloudReceivedAtMs ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Cloud received this evidence file at {s.formatTimingInstant(s.clipTiming.cloudReceivedAtMs)}.
        </Typography>
      ) : null}
    </Paper>
  );
}
